// Left-rail folder navigation built client-side from the flat folder list
// (parentId links). Collapsible; auto-expands the path to the current folder.
// Each row links to its folder route. "All documents" is the root.

import { useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import type { FolderMeta } from "../types";

interface Props {
  folders: FolderMeta[];
  currentFolderId: string | null;
}

export function FolderTree({ folders, currentFolderId }: Props) {
  const childrenOf = useMemo(() => {
    const map = new Map<string | null, FolderMeta[]>();
    for (const f of folders) {
      const key = f.parentId ?? null;
      const list = map.get(key) ?? [];
      list.push(f);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return map;
  }, [folders]);

  // Expand the ancestors of the current folder by default.
  const initialExpanded = useMemo(() => {
    const set = new Set<string>();
    const current = folders.find((f) => f.folderId === currentFolderId);
    if (current) {
      for (const a of current.ancestors ?? []) set.add(a);
      set.add(current.folderId);
    }
    return set;
  }, [folders, currentFolderId]);

  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const renderLevel = (parentId: string | null, depth: number) => {
    const kids = childrenOf.get(parentId) ?? [];
    if (kids.length === 0) return null;
    return (
      <ul className="folder-tree-list">
        {kids.map((f) => {
          const hasKids = (childrenOf.get(f.folderId) ?? []).length > 0;
          const isOpen = expanded.has(f.folderId);
          const isCurrent = f.folderId === currentFolderId;
          return (
            <li key={f.folderId}>
              <div
                className={`folder-tree-row${isCurrent ? " is-current" : ""}`}
                style={{ "--depth": depth } as CSSProperties}
              >
                <button
                  type="button"
                  className="folder-tree-twisty"
                  aria-label={isOpen ? "Collapse" : "Expand"}
                  aria-expanded={isOpen}
                  onClick={() => toggle(f.folderId)}
                  style={{ visibility: hasKids ? "visible" : "hidden" }}
                >
                  <Icon name={isOpen ? "chevron-down" : "chevron-right"} size={14} />
                </button>
                <Link to={`/folder/${f.folderId}`} className="folder-tree-link">
                  <Icon name={isOpen && hasKids ? "folder-open" : "folder"} size={16} />
                  <span className="folder-tree-name">{f.name}</span>
                </Link>
              </div>
              {isOpen && renderLevel(f.folderId, depth + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <nav className="folder-tree" aria-label="Folders">
      <Link
        to="/"
        className={`folder-tree-row folder-tree-root${
          currentFolderId === null ? " is-current" : ""
        }`}
      >
        <Icon name="file" size={16} />
        <span className="folder-tree-name">All documents</span>
      </Link>
      {renderLevel(null, 0)}
    </nav>
  );
}
